import { UxGameServices } from 'src/app/core/services/ux-game/ux-game-services';
import { GameCompletionData, GameCompletionHandler } from './game-completion-handler';
import { GameAlert } from '../common/game-alert';
import { EventName } from 'src/app/core/services/analytics/event-names';
import { RoomDisplayResult } from 'src/app/shared/types/room-display-result';
import { RoomResultStatus, RoomStatus } from 'src/app/shared/types/room-status';
import { OperatorService } from 'src/app/core/services/operator/operator.service';
import { RoomModel } from 'src/app/model/room/room.model';
import { Round } from 'src/app/shared/types/round';
import { Room } from 'src/app/model/room/room';
import { ResultDrawerViewModel } from 'src/app/features/results/result-drawer/result-drawer-view-model';
import { ResultDrawerComponent } from 'src/app/features/results/result-drawer/result-drawer.component';
import { PAGE } from 'src/app/routes';
import { GameId } from 'src/app/model/game/game-id';

export class PracticeCompletionHandler implements GameCompletionHandler {
  private opponentId: string | undefined;
  private opponentName: string | undefined;
  private opponentScore = 0;

  constructor(
    private services: UxGameServices,
    private gameAlert: GameAlert,
    private gameId: GameId,
    private rewardCents: number,
    private entryFeeCents: number,
  ) {}

  public setOpponentResult(
    opponentId: string | undefined,
    opponentName: string,
    opponentScore: number,
  ): void {
    this.opponentId = opponentId;
    this.opponentName = opponentName;
    this.opponentScore = opponentScore;
  }

  public async endGameComplete(
    data: GameCompletionData,
    _isComplete: boolean,
    pauseGame: (pause: boolean) => void,
  ): Promise<void> {
    pauseGame(true);

    this.services.analyticsService.log(EventName.PracticeMatchFinished, {
      context: 'game-complete',
    });

    await this.submitScore(
      this.services.translocoService.translate('pages.game-component.submit-score-complete-title'),
      this.services.translocoService.translate(
        'pages.game-component.submit-score-complete-subtitle',
        {
          score: data.score,
        },
      ),
      data,
    );
  }

  public async endGameUserFinish(
    data: GameCompletionData,
    pauseGame: (pause: boolean) => void,
  ): Promise<void> {
    pauseGame(true);

    const header = this.services.translocoService.translate(
      'pages.game-component.finish-alert-header',
    );
    const message = this.services.translocoService.translate(
      'pages.game-component.submit-score-finish-message',
      {
        score: this.services.themeService.formatNumber(data.score),
      },
    );

    this.gameAlert?.finish(
      header,
      message,
      () => {
        pauseGame(false);
      },
      async (_message: string) => {
        this.services.analyticsService.log(EventName.PracticeMatchFinished, {
          context: 'user-finish',
        });
        this.showResultDrawer(data);
      },
    );
  }

  public async endGameDeadlineTimeout(
    data: GameCompletionData,
    pauseGame: (pause: boolean) => void,
  ): Promise<void> {
    pauseGame(true);

    this.services.analyticsService.log(EventName.PracticeMatchFinished, { context: 'timeout' });

    await this.submitScore(
      this.services.translocoService.translate(
        'pages.game-component.submit-score-deadline-message',
      ),
      this.services.translocoService.translate(
        'pages.game-component.submit-score-deadline-subtitle',
        {
          score: data.score,
        },
      ),
      data,
    );
  }

  private async submitScore(
    successTitle: string,
    successSubtitle: string,
    data: GameCompletionData,
  ) {
    const alert = await this.services.alertController.create({
      header: successTitle,
      message: successSubtitle,
      cssClass: 'alert',
      buttons: [
        {
          text: this.services.translocoService.translate('pages.game-component.continue'),
          cssClass: 'alert-button',
          handler: () => {
            alert.dismiss();
            this.showResultDrawer(data);
          },
        },
      ],
      backdropDismiss: false,
    });
    alert.present();
  }

  private async showResultDrawer(data: GameCompletionData) {
    try {
      const result = PracticeCompletionHandler.calcResult(data.score, this.opponentScore);

      if (OperatorService.IsDemo) {
        if (!this.isFree()) {
          const now = Date.now();
          const roomModel: RoomModel = {
            creationMethod: 'MATCH',
            logicVersion: 1,
            gameId: this.gameId,
            currency: 'USD',
            entryFee: this.entryFeeCents,
            joinDeadline: now,
            result: result,
            resultSent: true,
            reward: this.rewardCents,
            roundDeadline: now,
            roundState: Round.Finished,
            score: data.score,
            opponent: {
              nick: this.opponentName ?? '',
              roundDeadline: now,
              roundState: Round.Finished,
              startRoundDeadline: now,
              score: this.opponentScore,
            },
            sortDate: now,
            startRoundDeadline: now,
          };

          const room = new Room(roomModel, now.toString(), 'demo');
          this.services.demoRoomService.addRoom(room);
        }

        // Send post message to parent window
        if (result === RoomDisplayResult.Win) {
          this.services.operatorService.updateDemoBalance(this.rewardCents);
        } else if (result === RoomDisplayResult.Draw) {
          this.services.operatorService.updateDemoBalance(this.entryFeeCents);
        }
      }

      const resultDrawViewModel = new ResultDrawerViewModel(
        undefined,
        '',
        this.gameId,
        PracticeCompletionHandler.glowColorForResult(result),
        this.isFree(),
        PracticeCompletionHandler.centsToUSD(this.entryFeeCents),
        PracticeCompletionHandler.centsToUSD(this.rewardCents),
        data.score,
        PracticeCompletionHandler.roomResultForResult(result),
        Round.Finished,
        null,
        null,
        this.opponentId,
        this.opponentName,
        this.opponentScore,
        Round.Finished,
        result,
        RoomResultStatus.Result,
        this.services.translocoService,
        this.services.themeService,
      );

      const isMobile = this.services.platform.is('mobile');
      const modal = await this.services.modalController.create({
        component: ResultDrawerComponent,
        componentProps: { viewModel: resultDrawViewModel, isAfterGame: true, isFreeGame: true },
        initialBreakpoint: isMobile ? 1 : undefined,
        breakpoints: isMobile ? [0, 1] : undefined,
        handle: false,
        backdropDismiss: false,
        cssClass: isMobile ? 'mobile-rounded-modal' : 'desktop-rounded-modal',
      });

      await modal.present();
    } catch (error) {
      this.services.router.navigate([PAGE.RESULTS]);
    }
  }

  private isFree() {
    return this.entryFeeCents === 0;
  }

  private static calcResult(
    score: number | undefined,
    opponentScore: number | undefined,
  ): RoomDisplayResult {
    if (score === undefined || opponentScore === undefined) return RoomDisplayResult.Draw;
    if (score > opponentScore) return RoomDisplayResult.Win;
    else if (score < opponentScore) return RoomDisplayResult.Lose;
    else return RoomDisplayResult.Draw;
  }

  private static glowColorForResult(result: RoomDisplayResult): string {
    if (result === RoomDisplayResult.Win) return '--game-win';
    else if (result === RoomDisplayResult.Lose) return '--game-lose';
    else if (result === RoomDisplayResult.Draw) return '--game-draw';
    return '';
  }

  private static roomResultForResult(result: RoomDisplayResult): RoomStatus {
    if (result === RoomDisplayResult.Win) return RoomStatus.Won;
    else if (result === RoomDisplayResult.Lose) return RoomStatus.Lost;
    return RoomStatus.Draw;
  }

  private static centsToUSD(cents: number): string {
    return `$${(cents / 100).toFixed(2)}`;
  }
}
